import { AccommodationModalDetailed } from "@/components/AccommodationModalDetailed/AccommodationModalDetailed.tsx";
import Container from "@/components/Container/Container.tsx";
import { useLegalModalStore } from "@/components/LegalModal/store/useLegalModalStore.ts";
import Modal from "@/components/Modal/Modal.tsx";
import { Stepper } from "@/components/Stepper/Stepper.tsx";
import { getCampuccinoLinks } from "@/helpers/customFeatures.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { RateGroup } from "@/helpers/types/types.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { useModalState } from "@/helpers/useModalState.ts";
import { ReservationFinale } from "@/pages/ReservationFinale/ReservationFinale.tsx";
import { AccommodationOverview } from "@/pages/ReservationOverview/components/AccommodationOverview/AccommodationOverview.tsx";
import { HolderAndPaymentOverview } from "@/pages/ReservationOverview/components/HolderAndPaymentOverview/HolderAndPaymentOverview.tsx";
import { PaymentNote } from "@/pages/ReservationOverview/components/PaymentNote/PaymentNote.tsx";
import { ReservationOverviewSubmit } from "@/pages/ReservationOverview/components/ReservationOverviewSubmit/ReservationOverviewSubmit.tsx";
import { ReservationOverviewProps } from "@/pages/ReservationOverview/interfaces/ReservationOverviewProps.ts";
import { useReservationFinish } from "@/pages/ReservationOverview/useReservationFinish.ts";
import { useReservationSubmit } from "@/pages/ReservationOverview/useReservationSubmit.tsx";
import { getAPIUrl } from "@/stores/ApolloClient/helpers/getAPIUrl.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { MouseEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const ReservationOverview = (props: ReservationOverviewProps) => {
  const { t, i18n } = useTranslation();
  const { termsAndConditions, paymentEnabled, paymentType } = useGetOrganization();
  const {
    setPage,
    calculatedCartData,
    cartAccommodationItems,
    isGDPRHighlighted,
    acceptedGDPR,
    setAcceptedGDPR,
  } = useEngineStore(
    useShallow((state) => ({
      setPage: state.setPage,
      calculatedCartData: state.calculatedCartData,
      cartAccommodationItems: state.cartAccommodationItems,
      isGDPRHighlighted: state.isGDPRHighlighted,
      acceptedGDPR: state.acceptedGDPR,
      setAcceptedGDPR: state.setAcceptedGDPR,
    })),
  );

  const { modalState, handleCloseModal, handleOpenModal } = useModalState<RateGroup>();
  const handleOpenLegalModal = useLegalModalStore((state) => state.handleOpenModal);

  const { handleSubmitCart, submitCartLoading, isReserved, isPaymentRedirect } = useReservationSubmit(
    props.onSubmitComplete,
  );
  useReservationFinish(isReserved);

  const renderAccommodationOverviews = () => {
    return cartAccommodationItems?.map((item) => (
      <AccommodationOverview key={item.id} cartAccommodation={item} handleOpenGallery={handleOpenModal} />
    ));
  };

  const handleTermsOnClick = (e: MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleOpenLegalModal(termsAndConditions);
  };

  const renderTermsAndConditionsLabel = () => {
    if (getAPIUrl().includes("campuccino")) {
      const links = getCampuccinoLinks(i18n.language);
      return (
        <>
          {t("termsAndConditions.campuccinoAgree")}{" "}
          <a
            href={links.privacy}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            {t("termsAndConditions.campuccinoPrivacy")}
          </a>{" "}
          {t("termsAndConditions.campuccinoAnd")}{" "}
          <a
            href={links.terms}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            {t("termsAndConditions.campuccinoTerms")}
          </a>
        </>
      );
    }

    return (
      <>
        {t("termsAndConditions.accept")}{" "}
        <span onClick={handleTermsOnClick}>{t("termsAndConditions.terms")}</span>
      </>
    );
  };

  useEffect(() => {
    window.onbeforeunload = () => {
      if (useEngineStore.getState().isReserved || isPaymentRedirect.current) return;
      setPage(AppPage.HolderAndPaymentInfo, "replace");
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, [setPage, isPaymentRedirect]);

  return (
    <div>
      <>
        <Stepper />
        <main className="resOverviewWrap o-page">
          <div className="o-page__inner">
            <section className="c-reservation-overview-wrapper">
              <Container>
                <div className="c-reservation-overview">
                  <div className="c-reservation-overview__info">
                    <div className="c-accommodation-overview">
                      <HolderAndPaymentOverview />
                      <div>{renderAccommodationOverviews()}</div>
                      <div className="accommodation-overview__total">
                        <div className="accommodation-overview__total--price">
                          <h5>{t("total")}</h5>
                        </div>
                        <div className="accommodation-overview__total--note">
                          <h5>{formatCurrency(calculatedCartData?.priceBreakdown?.withDiscount || 0)}</h5>
                          <PaymentNote paymentEnabled={paymentEnabled} paymentType={paymentType} />
                        </div>
                      </div>
                      {/* ---- */}
                      <div className="c-accommodation-overview__gdpr-and-submit">
                        <div
                          className={`c-accommodation-overview__gdpr-and-submit__terms ${
                            isGDPRHighlighted ? "is-highlighted" : ""
                          }`}
                        >
                          <div className="formFieldRow c-be-booking-form__field c-be-booking-form__field--checkbox">
                            <input
                              id="terms-and-conditions"
                              name="terms-and-conditions"
                              type={"checkbox"}
                              checked={acceptedGDPR}
                              onChange={(e) => setAcceptedGDPR(e.target.checked)}
                            />
                            <label htmlFor="terms-and-conditions">{renderTermsAndConditionsLabel()}</label>
                          </div>
                        </div>
                        <ReservationOverviewSubmit
                          handleSubmitCart={handleSubmitCart}
                          submitCartLoading={submitCartLoading}
                          isReserved={isReserved}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Container>
            </section>
          </div>
        </main>
        {isReserved && <ReservationFinale />}
        <Modal isOpen={modalState.open} onClose={handleCloseModal}>
          <AccommodationModalDetailed
            hideRatesPicker={true}
            previewRG={modalState.editObject}
            handleCloseModal={handleCloseModal}
          />
        </Modal>
      </>
    </div>
  );
};
